import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {skipToken} from '@reduxjs/toolkit/query';

import {SkeletonItem, Stack} from '@edna/components/primitives';

import {Badge, Header} from 'src/components';
import {SecondaryText, Text} from 'src/components/primitives';
import {useGetBalanceQuery} from 'src/containers/Billing';
import LicensesList from 'src/containers/Licenses/LicensesList';
import {useGetAllLicensesQuery} from 'src/containers/Licenses/api';
import {useGetResellersQuery} from 'src/containers/Resellers';
import {ResponsibleManager, useGetAccountManagersQuery} from 'src/containers/ResponsibleManager';
import {
  AddAvailableTariffButton,
  AvailableTariffsList,
  ManagerTariffsList,
  useGetAvailableTariffsQuery,
} from 'src/containers/Tariffs';
import PATHS from 'src/paths';
import {getCountryLabel, parseISO} from 'src/utils';

import {useGetAgentClientQuery, useGetLeadClientQuery} from '../api';
import {ELeadClientStatus, TLeadClient, statusColorHash} from '../definitions';
import {getTaxNumberLabel, isLeadHasTenant} from '../utils';
import * as S from './style';

const Detail = React.memo<{value?: React.ReactNode; label: string}>(({value, label}) => {
  const {t} = useTranslation();
  const {id: leadClientId} = useParams<'id'>();

  const {isLoading} = useGetLeadClientQuery(leadClientId ?? skipToken);

  if (isLoading) {
    return (
      <S.Details>
        <S.DetailsLabel>{t(`LeadCompanyClients:leadClientCard.${label}`)}</S.DetailsLabel>
        <SkeletonItem width="65px" height="16px" />
      </S.Details>
    );
  }

  if (!value) {
    return (
      <S.Details>
        <S.DetailsLabel>{t(`LeadCompanyClients:leadClientCard.${label}`)}</S.DetailsLabel>
        {t('LeadCompanyClients:leadClientCard.empty')}
      </S.Details>
    );
  }

  return (
    <S.Details>
      <S.DetailsLabel>{t(`LeadCompanyClients:leadClientCard.${label}`)}</S.DetailsLabel>
      {value}
    </S.Details>
  );
});

Detail.displayName = 'Detail';

const LeadContactInfo = React.memo<{leadClient: TLeadClient}>(({leadClient}) => {
  const {t} = useTranslation();

  return (
    <section>
      <S.Subtitle>{t('LeadCompanyClients:leadClientCard.contact')}</S.Subtitle>
      <Detail label="contactName" value={leadClient.contactName} />
      <Detail label="phone" value={leadClient.phone} />
      <Detail label="email" value={leadClient.email} />
    </section>
  );
});

LeadContactInfo.displayName = 'LeadContactInfo';

const LeadCompanyInfo = React.memo<{leadClient: TLeadClient}>(({leadClient}) => {
  const {t} = useTranslation();

  const {data: resellers = []} = useGetResellersQuery();

  const taxNumberLabel = React.useMemo(() => getTaxNumberLabel({...leadClient}), [leadClient]);

  return (
    <section>
      <S.Subtitle>{t('LeadCompanyClients:leadClientCard.companyData')}</S.Subtitle>
      <Detail label="companyName" value={leadClient.companyName} />
      <Detail label={taxNumberLabel} value={leadClient.taxNumber} />
      <Detail label="ogrn" value={leadClient.ogrn} />
      <Detail label="country" value={getCountryLabel(t, leadClient.countryCode)} />
      <Detail label="currency" value={leadClient.currencyCode} />
      <Detail
        label="reseller"
        value={resellers?.find((item) => item.id === leadClient.resellerId)?.name}
      />
      <Detail
        label="document"
        value={
          leadClient.documentDate
            ? t('LeadCompanyClients:leadClientCard.documentName', {
                documentNumber: leadClient.documentNumber,
                documentDate: parseISO(leadClient.documentDate, 'dd.MM.yyyy'),
              })
            : leadClient.documentNumber
        }
      />
    </section>
  );
});

LeadCompanyInfo.displayName = 'LeadCompanyInfo';

const LeadPaymentsInfo = React.memo<{leadClient: TLeadClient}>(({leadClient}) => {
  const {t} = useTranslation();

  const {data: tenantBalance} = useGetBalanceQuery(leadClient.reservedTenantId, {
    skip: !isLeadHasTenant(leadClient.status),
  });
  const {data: accountManagers = []} = useGetAccountManagersQuery();

  return (
    <section>
      <S.Subtitle>{t('LeadCompanyClients:leadClientCard.settlementRules')}</S.Subtitle>
      <Detail
        label="paymentType"
        value={t(`LeadCompanyClientEdit:paymentTypes.${leadClient.paymentType}.label`)}
      />
      <Detail
        label="managerResponsibleForCheckingId"
        value={
          accountManagers?.find((item) => item.id === leadClient.managerResponsibleForCheckingId)
            ?.name
        }
      />
      <Detail
        label="depositAmount"
        value={
          <S.Price
            currency={leadClient.currencyCode}
            value={Math.abs(leadClient.securityDeposit ?? 0)}
            size="S"
          />
        }
      />
      <Detail
        label="overdraftAmount"
        value={
          <S.Price
            currency={leadClient.currencyCode}
            value={Math.abs(tenantBalance?.maxOverdraft ?? leadClient.maxOverdraft ?? 0)}
            size="S"
          />
        }
      />
      <Detail
        label="activateLicenseDebt"
        value={t(
          `LeadCompanyClients:leadClientCard.${
            leadClient.activateLicenseDebt ? 'allowed' : 'notAllowed'
          }`,
        )}
      />
      <Detail
        label="paymentPeriod"
        value={`${leadClient.licensePaymentTerm} ${t('Common:time.days', {
          count: leadClient.licensePaymentTerm,
        })}`}
      />
      <Detail
        label="cancellationPeriod"
        value={`${leadClient.termBeforeTerminationLicense} ${t('Common:time.days', {
          count: leadClient.termBeforeTerminationLicense,
        })}`}
      />
      <S.Details>
        <S.DetailsLabel>{t('LeadCompanyClientEdit:labels.documentType')}</S.DetailsLabel>
        {t(`LeadCompanyClientEdit:documentTypes.${leadClient.documentType}.label`)}
      </S.Details>
    </section>
  );
});

LeadPaymentsInfo.displayName = 'LeadPaymentsInfo';

const LicensesAndTariffsInfo = React.memo<{leadClient: TLeadClient}>(({leadClient}) => {
  const {t} = useTranslation();

  const tenantId = leadClient.reservedTenantId;
  const canEditTariffs =
    !leadClient.billingDisabled &&
    [ELeadClientStatus.REQUIRES_ATTENTION, ELeadClientStatus.CANCELLED].includes(leadClient.status);

  const {data: allLicenses = []} = useGetAllLicensesQuery(tenantId, {
    skip: !isLeadHasTenant(leadClient.status),
  });
  const {data: tariffs = []} = useGetAvailableTariffsQuery({tenantId}, {skip: !tenantId});
  const isShowTariffs = !!tariffs.length || canEditTariffs;

  return (
    <>
      {!!allLicenses.length && (
        <S.Section noMargin>
          <S.Subtitle>{t('LeadCompanyClients:leadClientCard.companyLicenses')}</S.Subtitle>
          <LicensesList tenantId={tenantId} />
        </S.Section>
      )}
      {isShowTariffs && (
        <S.TariffsContainer>
          <Text weight={500}>{t('LeadCompanyClients:leadClientCard.tariffs')}</Text>
          {canEditTariffs && <AddAvailableTariffButton />}
          {canEditTariffs ? (
            <ManagerTariffsList tenantId={tenantId} />
          ) : (
            <AvailableTariffsList tenantId={tenantId} />
          )}
        </S.TariffsContainer>
      )}
    </>
  );
});

LicensesAndTariffsInfo.displayName = 'LicensesAndTariffsInfo';

const LeadCompanyClientCard = React.memo(() => {
  const {t} = useTranslation();
  const {id: leadClientId} = useParams<'id'>();

  const {data: leadClient = {} as TLeadClient, isSuccess} = useGetLeadClientQuery(
    leadClientId ?? skipToken,
  );

  const {data: agent} = useGetAgentClientQuery(
    leadClient.partnerId ? {tenantId: leadClient.partnerId} : skipToken,
  );

  return (
    <>
      <Header
        title={leadClient?.companyName || leadClient?.email}
        backToUrl={PATHS.COMPANY_CLIENTS}
      />
      {!!leadClient?.selfRegistered && (
        <S.Section>
          <Stack direction="ROW" size="1" align="center">
            <Detail
              label="leadType"
              value={t('LeadCompanyClients:leadClientCard.selfRegistered')}
            />
            <Badge color={statusColorHash[leadClient.status]}>
              {t(`LeadCompanyClients:status.${leadClient.status}`)}
            </Badge>
          </Stack>
        </S.Section>
      )}
      {!!agent && (
        <S.Section>
          <SecondaryText>{t('LeadCompanyClients:leadClientCard.partner')}</SecondaryText>
          <S.ManagerName>{agent.agentName}</S.ManagerName>
        </S.Section>
      )}
      <ResponsibleManager responsibleManagerId={leadClient.responsibleManagerId} />
      <Stack size="5" isFullWidth>
        <LeadContactInfo leadClient={leadClient} />
        <LeadCompanyInfo leadClient={leadClient} />
        <LeadPaymentsInfo leadClient={leadClient} />
        {isSuccess && <LicensesAndTariffsInfo leadClient={leadClient} />}
      </Stack>
    </>
  );
});

LeadCompanyClientCard.displayName = 'LeadCompanyClientCard';

export {LeadContactInfo, LeadCompanyInfo, LeadPaymentsInfo};
export default LeadCompanyClientCard;
